import React from 'react';
import { Link } from 'react-router-dom';

/**
 * UiSummaryCard — the ONE canonical summary/KPI card for the whole POS.
 *
 * Locked spec (matches the reference pages Orders / Reports / Reconciliation):
 *   card   : bg-white rounded-xl p-3 sm:p-4 shadow-sm border   (plain gray border only)
 *   label  : text-[10px] sm:text-xs text-gray-500
 *   value  : text-sm sm:text-lg font-bold  (+ optional tone color on the value text)
 *
 * Deliberately NO icon prop and NO colored background / colored border — those are
 * banned on summary cards. The value may be tinted via `tone` (e.g. 'text-blue-600').
 * Fill it via props so every card across every page is pixel-identical.
 *
 * Props:
 *   label  — small gray heading
 *   value  — main value (string/number/node). Ignored if `lines` is given.
 *   tone   — optional Tailwind text-color class for the value (default text-gray-800)
 *   lines  — optional array of strings/nodes for multi-line values (e.g. a date range),
 *            rendered small; use instead of `value` when a card shows stacked lines.
 *   sub    — optional small gray note under the value.
 *   to     — optional react-router path; makes the whole card a clickable link
 *            (adds a subtle hover shadow only — never a color/icon).
 *   className — extra classes on the card wrapper (layout only; do not add color)
 */
export function UiSummaryCard({ label, value, tone = 'text-gray-800', lines = null, sub = '', to = null, onClick = null, className = '' }) {
  const Wrapper = to ? Link : 'div';
  const wrapperProps = to ? { to } : (onClick ? { onClick } : {});
  const clickable = (to || onClick) ? ' hover:shadow-md transition-shadow cursor-pointer' : '';
  return (
    <Wrapper {...wrapperProps} className={`block bg-white rounded-xl p-3 sm:p-4 shadow-sm border ${clickable} ${className}`}>
      <p className="text-[10px] sm:text-xs text-gray-500">{label}</p>
      {lines ? (
        lines.map((line, i) => (
          <p key={i} className="text-xs font-medium text-gray-800">{line}</p>
        ))
      ) : (
        <p className={`text-sm sm:text-lg font-bold ${tone}`}>{value}</p>
      )}
      {sub && <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">{sub}</p>}
    </Wrapper>
  );
}

/**
 * UiSummaryCards — grid wrapper so the row spacing is consistent too.
 * Default is the reference 2-up / 4-up responsive grid; override `cols` for other counts.
 */
export function UiSummaryCards({ cols = 'grid-cols-2 md:grid-cols-4', className = '', children }) {
  return (
    <div className={`grid ${cols} gap-3 sm:gap-4 ${className}`}>
      {children}
    </div>
  );
}

export default UiSummaryCard;
